import {
  CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY,
  CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY,
} from "hassibot/current-legal-entity.constants";
import { get } from "hassibot/local-storage";

import { LegalEntityUuid, LegalMode } from "./services_v2/common/types";

/**
 * In-memory cache over localStorage, invalidated only by this tab's own entity
 * switch: localStorage is shared across tabs, and another tab's switch must
 * not change what this tab requests and renders.
 *
 * localStorage + types imports only: the API client imports this module.
 */

let hasReportedPreSeedRead = false;

const reportPreSeedRead = (): void => {
  const message =
    "[legal-mode] legal mode read before the localStorage seed: read the mode only " +
    "after CurrentLegalEntityProvider has seeded the legal entity.";
  if (import.meta.env.MODE === "development") {
    throw new Error(message);
  }
  if (import.meta.env.MODE === "test" || hasReportedPreSeedRead) return;
  hasReportedPreSeedRead = true;
  console.warn(message);
  void import("@sentry/react").then(Sentry => Sentry.captureMessage(message)).catch(() => {});
};

let cachedLegalMode: LegalMode | null = null;
let cachedLegalEntityUuid: LegalEntityUuid | null = null;

/**
 * Read at access time, never baked at module evaluation — module-scope values
 * must go through `perMode()` (`legal-mode-lazy.ts`). Only read AFTER the
 * provider's localStorage seed: a pre-seed read silently breaks first-open
 * branding / i18n, hence the backstop. A pre-seed `null` is never pinned.
 */
export const getCurrentLegalMode = (): LegalMode | null => {
  if (cachedLegalMode !== null) return cachedLegalMode;
  const mode = get(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY) as LegalMode | null;
  if (mode === null) {
    reportPreSeedRead();
    return null;
  }
  cachedLegalMode = mode;
  return mode;
};

/**
 * Unlike the mode, a pre-seed `null` is legitimate: before the seed only the
 * session bootstrap (`/me`, entity-agnostic) can fire — the provider gates the
 * app on the seed — so the client omits the `X-LEGAL-ENTITY-UUID` header.
 */
export const getCurrentLegalEntityUuid = (): LegalEntityUuid | null => {
  cachedLegalEntityUuid ??= get(CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY) as LegalEntityUuid | null;
  return cachedLegalEntityUuid;
};

/** MUST be called by every code path that rewrites the stored legal entity. */
export const invalidateLegalEntitySessionCache = (): void => {
  cachedLegalMode = null;
  cachedLegalEntityUuid = null;
};
